import React, { useState, useEffect, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { Table, Button, FloatingLabel, Form, Stack } from 'react-bootstrap';
import 'react-toastify/dist/ReactToastify.css';
import Vendor from '../../components/common/vendor';
import Factory from '../../components/factory';
import { get } from '../../services/apiController';
import PaginationInPage from '../Pagination';
import { TABLE_DEFAULT_LIMIT } from '../../util/constant';
import Item from '../../components/common/item';
import { GrDocumentExcel } from 'react-icons/gr';
import { get_auth_key, getStoredData } from '../../util';
import DatePickerComponent from '../../components/common/date-picker';
import { useForm } from 'react-hook-form';

const getQueryParams = (form, download, currentPage) => {
  const queryParams = {};
  if (form.getValues('vendorId')) {
    queryParams.vendorId = form.getValues('vendorId');
  }
  if (form.getValues('factoryId')) {
    queryParams.factoryId = form.getValues('factoryId');
  }
  if (form.getValues('itemId')) {
    queryParams.itemId = form.getValues('itemId');
  }
  if (form.getValues('dateFrom')) {
    queryParams.dateFrom = form.getValues('dateFrom');
  }
  if (form.getValues('dateTo')) {
    queryParams.dateTo = form.getValues('dateTo');
  }
  // When it is not a download
  if (download) {
    queryParams.download = download;
  } else {
    queryParams.page = currentPage;
    queryParams.limit = TABLE_DEFAULT_LIMIT;
  }
  return queryParams;
};

const GRNConsolidatedReport = () => {
  const storedData = JSON.parse(getStoredData(get_auth_key()));
  const admins = ['admin', 'super-admin', 'inventory-admin'].includes(storedData.role);
  const [data, setData] = useState([]);
  const clickToDownload = useRef(null);

  const form = useForm({
    defaultValues: {
      page: 1,
      vendorId: '',
      factoryId: !admins ? storedData?.locationId[0] || '' : '',
      dateFrom: '',
      dateTo: '',
      itemId: '',
    },
  });

  const watch = form.watch(['dateFrom', 'dateTo', 'factoryId', 'vendorId', 'itemId']);

  const [factories, setFactories] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const downloadFile = (res) => {
    clickToDownload.current.href = `${process.env.REACT_APP_BASE_URL}/file/download?filePath=${res.data.filePath}`;
    setTimeout(() => {
      clickToDownload.current.click();
    }, 500);
  };

  const getConsolidatedReport = (download = false) => {
    const filters = new URLSearchParams(getQueryParams(form, download, form.getValues('page')));
    const url = `/inv/report/grn-consolidated/?${filters}`;
    get(url)
      .then((res) => {
        if (download) {
          downloadFile(res);
        } else {
          setData(res?.data?.data);
          setTotalRecords(res?.data.count);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data.data.message || err?.message);
      });
  };

  function getFactories() {
    try {
      get(`/branches/?type=factory`).then((response) => {
        setFactories(response?.data?.rows);
      });
    } catch (err) {
      toast.error(err?.response?.data?.data?.message || err?.message);
    }
  }

  useEffect(() => {
    getFactories();
  }, []);

  useEffect(() => {
    form.setValue('page', 1);
    getConsolidatedReport();
  }, [watch[0], watch[1], watch[2], watch[3], watch[4]]);

  return (
    <>
      <ToastContainer />
      <div className="card">
        {/* Filters */}
        <div className="d-flex justify-content-between align-items-center w-100 card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <div style={{ flex: 1 }}>
            <h4 className="card-title" style={{ fontSize: '16px' }}>
              GRN Consolidated Report | {totalRecords ?? 0}
            </h4>
          </div>
          <div style={{ flex: 2 }}>
            <div className="row w-100 justify-content-end">
              <div className="col-lg-3">
                <Vendor
                  onSelect={(vendor) => {
                    form.setValue('vendorId', vendor?.value || '');
                  }}
                />
              </div>
              <div className="col-lg-3">
                <FloatingLabel label="Factory" className="mb-2">
                  <Factory
                    enableAllOption
                    factoryId={form.getValues('factoryId')}
                    disabled={!admins}
                    onChange={(e) => {
                      form.setValue('factoryId', e.target.value);
                    }}
                    factories={factories}
                    label="factory"
                    name="factoryId"
                  />
                </FloatingLabel>
                <Item
                  onSelect={(item) => {
                    form.setValue('itemId', item?.value || '');
                  }}
                />
              </div>
              <div className="col-lg-3">
                <DatePickerComponent
                  selectedDate={form.getValues('dateFrom')}
                  placeholderText="From Date"
                  onDateChange={(date) => {
                    form.setValue('dateFrom', date || '');
                  }}
                />
                <div className="mt-2"></div>
                <DatePickerComponent
                  selectedDate={form.getValues('dateTo')}
                  placeholderText="To Date"
                  onDateChange={(date) => {
                    form.setValue('dateTo', date || '');
                  }}
                />
              </div>
              <div className="col-lg-3">
                <Stack gap={2}>
                  <GrDocumentExcel
                    title="Download Excel"
                    className="react-icons green mt-2"
                    style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                    onClick={() => getConsolidatedReport(true)}
                  />
                  <a ref={clickToDownload} href="/" target="_blank" className="hiddenItem">
                    HiddenClick
                  </a>
                </Stack>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              <Table responsive striped bordered>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Vendor</th>
                    <th>Factory</th>
                    <th>Item</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={item.id}>
                      <td>{(form.getValues('page') - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                      <td>{item.vendor_name}</td>
                      <td>{item.branch_name}</td>
                      <td>{item.item_name}</td>
                      <td>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <PaginationInPage
                currentPage={form.getValues('page')}
                setCurrentPage={(page) => {
                  form.setValue('page', page);
                  getConsolidatedReport();
                }}
                totalRecords={totalRecords}
                TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GRNConsolidatedReport;
